import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Crown, Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/contexts/I18nContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AmbassadorOffer() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <section
      data-testid="ambassador-offer"
      id="ambassador"
      className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16"
    >
      <div className="dncm-hud-frame green rounded-lg p-8 sm:p-12 bg-black/60 text-center">
        <div className="inline-flex items-center gap-2 font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/65">
          <Crown className="w-3.5 h-3.5 text-[color:var(--dncm-green)]" />
          {t("ambassador_eyebrow")}
        </div>
        <h3 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
          {lang === "fr" ? (
            <>
              Devenez{" "}
              <span className="dncm-green-glow text-[color:var(--dncm-green)]">Ambassadeur</span>{" "}
              DNCM Vision.
            </>
          ) : (
            <>
              Become a{" "}
              <span className="dncm-green-glow text-[color:var(--dncm-green)]">DNCM Vision</span>{" "}
              Ambassador.
            </>
          )}
        </h3>
        <p className="mt-3 text-white/65 max-w-2xl mx-auto">
          {lang === "fr"
            ? "Accès privilégié, tarif fondateur, suivi personnalisé. Idéal pour tester une escouade complète sans engagement long."
            : "Privileged access, founder pricing, personal follow-up. Perfect to test a full squad without long-term commitment."}
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            data-testid="ambassador-activate"
            onClick={() => setOpen(true)}
            className="inline-flex items-center justify-center gap-3 px-8 sm:px-10 py-5 rounded-full font-mono-d text-[12px] sm:text-[13px] uppercase tracking-[0.28em] dncm-offer-btn"
          >
            <Sparkles className="w-4 h-4" />
            {t("ambassador_banner")}
          </button>
        </div>

        <div className="mt-4 font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45">
          {lang === "fr"
            ? "Paiement sécurisé · GCash · Maya · Carte · WhatsApp Pay"
            : "Secure payment · GCash · Maya · Card · WhatsApp Pay"}
        </div>
      </div>

      <AmbassadorDialog open={open} onOpenChange={setOpen} />
    </section>
  );
}

function AmbassadorDialog({ open, onOpenChange }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(lang === "fr" ? "Merci d'indiquer votre nom." : "Please share your name.");
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        message: form.message.trim() || null,
        source: "ambassador",
        agent: "Ambassadeur 1000PHP",
      });
      toast.success(t("success_amb"));
      setForm({ name: "", email: "", message: "" });
      onOpenChange(false);
    } catch {
      toast.error(lang === "fr" ? "Envoi impossible. Réessayez ou utilisez WhatsApp." : "Could not send. Try again or use WhatsApp.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#070a0c] border border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">{t("ambassador_banner")}</DialogTitle>
          <DialogDescription className="text-white/65">
            {lang === "fr"
              ? "Laissez vos coordonnées — nous finalisons votre activation par retour."
              : "Drop your details — we finalize your activation right after."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="space-y-1.5">
            <Label htmlFor="amb-name" className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">{t("name")}</Label>
            <Input id="amb-name" data-testid="amb-name" value={form.name} onChange={update("name")}
              className="bg-black/40 border-white/15 text-white focus-visible:ring-[color:var(--dncm-green)]" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="amb-email" className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">{t("email")}</Label>
            <Input id="amb-email" type="email" data-testid="amb-email" value={form.email} onChange={update("email")}
              className="bg-black/40 border-white/15 text-white focus-visible:ring-[color:var(--dncm-green)]" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="amb-msg" className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">{t("message")}</Label>
            <Input id="amb-msg" data-testid="amb-msg" value={form.message} onChange={update("message")}
              className="bg-black/40 border-white/15 text-white focus-visible:ring-[color:var(--dncm-green)]" />
          </div>
          <button type="submit" data-testid="amb-submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-mono-d text-[11px] uppercase tracking-[0.22em] dncm-offer-btn disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {t("confirm_order")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
